import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  IconX,
  IconEye,
  IconBook,
  IconXboxX,
  IconSearch,
  IconChecks,
  IconMoodKid,
  IconLockOpen,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import { ESchoolStatus, ERestoreEntityType } from '@/enum';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getClassDropdown,
  blockUnblockRecordAPI,
} from '@/service/commonService';
import {
  getTeacherLessonList,
  getTeacherLessonSummary,
} from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import {
  IStatsDataItem,
  ITeacherLessonData,
  ILessonFilterValues,
  ISelectedLessonValue,
  ITeacherLessonSummaryData,
} from '@/types';
import { getStartOfMonthUnix } from '@/utils';

import BlockLessonModal from './modals/BlockLessonModal';
import LessonDetailsModal from './modals/LessonDetailsModal';
import LessonFilterModal from './modals/LessonFilterModal';

const LESSON_SUMMARY_CONFIG = [
  {
    key: 'total_lessons',
    icon: <IconBook color="#717784" />,
    label: 'teacher.summary-cards.overall-lessons',
  },
  {
    key: 'total_assigned_students',
    icon: <IconMoodKid color="#717784" />,
    label: 'teacher.summary-cards.total-assigned-students',
  },
  {
    key: 'completed_lessons',
    icon: <IconChecks color="#717784" />,
    label: 'teacher.summary-cards.completed-lessons',
  },
  {
    key: 'expired_lessons',
    icon: <IconX color="#717784" />,
    label: 'teacher.summary-cards.expired-lessons',
  },
];

const TeacherLessonList = () => {
  const { id } = useParams<{ id: string }>();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();
  const { settings } = useSettingsStore();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);

  const [isLessionDetailsOpen, setIsLessionDetailsOpen] = useState(false);
  const [isBlockLessonModalOpen, setIsBlockLessonModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] =
    useState<ISelectedLessonValue | null>(null);
  const {
    loadingTable,
    setLoadingTable,
    loadingSummary,
    setLoadingSummary,
    loadingModal,
    setLoadingModal,
  } = useCommonStore();

  const [lessons, setLessons] = useState<ITeacherLessonData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [filterValues, setFilterValues] = useState<ILessonFilterValues>({
    status: '',
    class_id: [],
  });
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    const fetchClasses = async () => {
      const response = await getClassDropdown(
        id ? { teacher_id: id } : undefined
      );
      if (response?.status) {
        const options =
          response?.data?.map((item: { id: string; name: string }) => ({
            value: item?.id,
            label: item?.name,
          })) || [];
        setClassOptions(options);
      }
    };
    fetchClasses();
  }, [id]);

  const [statsData, setStatsData] = useState<IStatsDataItem[]>(
    LESSON_SUMMARY_CONFIG.map((item) => ({
      ...item,
      value: '0',
      trend: '0%',
      trendLabel: formatText('common.this-month'),
      trendDirection: 'STABLE',
    }))
  );

  useEffect(() => {
    setTitle(formatText('sidebar.lesson-list'));
    setIsBreadcrumb([
      { label: formatText('sidebar.user-management') },
      { label: formatText('sidebar.teachers'), to: '/user-management/teacher' },
      { label: formatText('sidebar.lesson-list') },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.language]);

  const fetchLessons = async () => {
    if (!id) return;
    setLoadingTable(true);
    const params = {
      search: debouncedSearch || undefined,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      order: sortField ? { [sortField]: sortDirection } : undefined,
      status: filterValues?.status || undefined,
      class_id: filterValues?.class_id?.length
        ? filterValues?.class_id
        : undefined,
      // stages: filterValues?.levels?.length ? filterValues?.levels : undefined,
    };
    const response = await getTeacherLessonList(id, params);
    if (response?.status) {
      setLessons(response?.data || []);
      setTotal(response?.total || 0);
    }
    setLoadingTable(false);
  };

  const fetchSummary = async () => {
    if (!id) return;
    setLoadingSummary(true);
    const monthTimestamp = getStartOfMonthUnix();

    const response = await getTeacherLessonSummary(id, {
      month: monthTimestamp,
    });

    if (response?.status) {
      const apiData = response?.data as ITeacherLessonSummaryData;
      const mappedSummary = LESSON_SUMMARY_CONFIG?.map((item) => {
        const data = apiData[item?.key as keyof ITeacherLessonSummaryData];
        return {
          ...item,
          label: formatText(item?.label),
          value: (data?.total || 0)?.toLocaleString(),
          trend: `${data?.percentage || 0}%`,
          trendLabel: formatText('common.this-month'),
          trendDirection: (data?.trend || 'stable').toUpperCase(),
          isPositive: data?.is_positive,
        };
      });
      setStatsData(mappedSummary);
    }
    setLoadingSummary(false);
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, settings.language]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterValues]);

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    debouncedSearch,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    filterValues,
  ]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'user-management.lesson.column.name',
      key: 'title',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('title'),
      render: (row: ITeacherLessonData) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row?.id,
                name: row?.title,
                image: row?.lesson_img || '',
              },
            ]}
            size={40}
            avatarClassName="w-8 h-8 text-xs!"
            isSquare
          />
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-neutral-primary">
              {row?.title}
            </span>
            <span className="text-sm font-medium text-neutral-secondary">
              {formatText('user-management.lesson.column.created')}:{' '}
              {row?.created_at ? formatDate(row.created_at) : '-'}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'user-management.lesson.column.class',
      key: 'class',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('class'),
      render: (row: ITeacherLessonData) =>
        row?.classes?.map((c) => c?.name).join(', ') || '-',
    },
    // {
    //   title: 'user-management.lesson.column.level',
    //   key: 'stage',
    //   width: '15%',
    //   sorter: true,
    //   handleSort: () => handleSort('stage'),
    //   render: (row: { stage: ELessonStageType }) => {
    //     const levelConfig = LessonStageConfig[row.stage] ?? undefined;
    //     return (
    //       <span
    //         className={`px-2 py-1 w-fit rounded-full text-xs font-semibold whitespace-nowrap ${
    //           levelConfig?.className ?? ''
    //         }`}
    //       >
    //         {levelConfig?.label ? formatText(levelConfig.label) : row?.stage}
    //       </span>
    //     );
    //   },
    // },
    {
      title: 'user-management.lesson.column.student',
      key: 'student_count',
      width: '10%',
      sorter: true,
      handleSort: () => handleSort('student_count'),
      render: (row: ITeacherLessonData) => (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-semibold bg-indigo-light-50 text-indigo-secondary'
          )}
        >
          {row?.student_count || 0}
        </span>
      ),
    },
    {
      title: 'user-management.lesson.column.progress',
      key: 'progress',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('progress'),
      render: (row: ITeacherLessonData) => (
        <ProgressBar value={row?.progress ?? 0} showPercentage />
      ),
    },
    {
      title: 'user-management.lesson.column.status',
      key: 'status',
      width: '5%',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: ITeacherLessonData) => {
        const is_active = row?.status?.toLowerCase() === ESchoolStatus.ACTIVE;
        const is_in_active =
          row?.status?.toLowerCase() === ESchoolStatus.IN_ACTIVE;
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              is_active
                ? 'bg-success-light-50 text-success-secondary'
                : is_in_active
                  ? 'bg-warning-light-50 text-warning-secondary'
                  : 'bg-error-lite-50 text-error-secondary'
            )}
          >
            {is_active
              ? formatText('common.actions.active')
              : is_in_active
                ? formatText('common.status.inactive')
                : formatText('common.actions.blocked')}
          </span>
        );
      },
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '5%',
      align: 'center',
      render: (row: ISelectedLessonValue) => {
        const isBlocked = row?.status === ESchoolStatus.IN_ACTIVE;
        const lesson = {
          title: row?.title,
          id: row?.id,
          status: row?.status,
        };

        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedLesson(lesson);
              setIsLessionDetailsOpen(true);
            },
          },
          {
            key: isBlocked ? 'unblock' : 'block',
            label: isBlocked
              ? 'common.actions.unblock'
              : 'common.actions.block',
            icon: isBlocked ? (
              <IconLockOpen size={18} />
            ) : (
              <IconXboxX size={18} />
            ),
            isDanger: true,
            onClick: () => {
              if (isBlocked) {
                setSelectedLesson(lesson);
                handleBlockLesson(lesson);
              } else {
                setSelectedLesson(lesson);
                setIsBlockLessonModalOpen(true);
              }
            },
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const handleFilterApply = (filters: ILessonFilterValues) => {
    setFilterValues(filters);
  };

  const handleBlockLesson = async (lesson = selectedLesson) => {
    if (!lesson) return;

    setLoadingModal(true);

    try {
      const params = {
        id: lesson.id,
        record_type: ERestoreEntityType.LESSON,
        is_block: lesson.status === ESchoolStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        fetchLessons();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
      setIsBlockLessonModalOpen(false);
      setSelectedLesson(null);
    }
  };
  const isFilterApplied =
    !!filterValues?.status || (filterValues?.class_id?.length ?? 0) > 0;

  const showListUI =
    loadingTable ||
    lessons.length > 0 ||
    search.length > 0 ||
    debouncedSearch.length > 0 ||
    isFilterApplied;

  return (
    <>
      {showListUI && (
        <div className="flex flex-wrap justify-start items-center gap-2.5 mb-6 pl-1">
          <Input
            name="search"
            placeholder={formatText('user-management.lesson.search-by')}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-77 md:min-w-77 w-full"
            inputClassName="py-[13px]"
          />
          <div className="relative">
            <Button
              icon={<IconAdjustments size={20} />}
              label={formatText('common.actions.filter')}
              variant="outline"
              className={clsx(
                'px-4 h-12 cursor-pointer',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterModalOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>
      )}

      {showListUI && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {statsData?.map((stat, index) => (
            <SummaryCard
              key={index}
              variant="dashboard"
              label={formatText(stat.label)}
              value={stat?.value}
              icon={stat?.icon}
              trendDirection={stat?.trendDirection}
              isPositive={stat?.isPositive}
              trendLabel={
                stat?.trendLabel ? formatText(stat.trendLabel) : undefined
              }
              trend={stat?.trend}
              isLoading={loadingSummary}
            />
          ))}
        </div>
      )}

      <div className="mt-6">
        <CustomTable
          isLoading={loadingTable}
          dataSource={lessons}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          fieldName={sortField}
          sortDirection={sortDirection}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          onEmptyBtnClick={() => {}}
          isEmpty={
            !loadingTable &&
            lessons?.length === 0 &&
            search?.length === 0 &&
            debouncedSearch?.length === 0 &&
            !isFilterApplied
          }
          emptyTitle={formatText('user-management.lesson.not-found-title')}
          emptyDescription={formatText('user-management.lesson.not-found-desc')}
          isEmptySearch={
            lessons?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          onRowClick={(id) => {
            const lesson = lessons?.find((item) => item?.id === id);
            if (lesson) {
              setSelectedLesson({
                id: lesson.id,
                title: lesson.title,
                status: lesson.status as ISelectedLessonValue['status'],
              });
              setIsLessionDetailsOpen(true);
            }
          }}
        />
      </div>

      {isLessionDetailsOpen && (
        <LessonDetailsModal
          setIsOpen={setIsLessionDetailsOpen}
          lesson_id={selectedLesson?.id ?? ''}
        />
      )}
      {isBlockLessonModalOpen && (
        <BlockLessonModal
          setIsOpen={setIsBlockLessonModalOpen}
          selectedLesson={selectedLesson}
          isBlockLoading={loadingModal}
          onSubmit={handleBlockLesson}
        />
      )}
      {isFilterModalOpen && (
        <LessonFilterModal
          setIsOpen={setIsFilterModalOpen}
          onApply={handleFilterApply}
          initialValues={filterValues}
          classOptions={classOptions}
        />
      )}
    </>
  );
};

export default TeacherLessonList;
